import React from "react";
import styled, { createGlobalStyle } from "styled-components";
import { Flex } from "./Flex";
import { Spacing } from "../theme/Spacing";
import { Link, useLocation } from "react-router-dom";
import { Rounded } from "../theme/Rounded";
import { FontSize } from "./Text";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { AtiraIcon } from "./AtiraIcon";
import { Tooltip } from "../utils/local-antd";
import useWindowDimensions from "../hooks/useWindowDimensions";
import { Breakpoints } from "../theme/Breakpoints";
import { useAppSelector } from "../redux/store";
import { userSliceSelectors } from "../redux/user/user.selector";
import isEmpty from "lodash/isEmpty";
import { TestIDs } from "../tests/types/testIds";

const GlobalStyle = createGlobalStyle`
  #outlet-wrapper {
    padding-bottom: 0;
  }
`;

const SideMenu = styled(Flex)<{ isTherePSA: boolean }>`
  background-color: ${({ theme }) => theme.white};
  flex-direction: column;
  box-shadow: 0.125rem 0.25rem 0 rgba(0, 0, 0, 0.075);
  flex-shrink: 0;
  position: absolute;
  /* 3rem  = The header's height, 6rem = The PSAUnreadBanner's height */
  top: 0;
  bottom: 0;
  z-index: 2;
  /* 5rem  = The OutletWrapper's bottom padding in Layout.tsx, 6rem = The PSAUnreadBanner's height */
  height: 100%;
  width: 18rem;

  html[dir="rtl"] & {
    box-shadow: -0.125rem 0.25rem 0 rgba(0, 0, 0, 0.075);
  }

  @media (max-width: ${Breakpoints.DESKTOP}) {
    width: 4rem;
  }
`;

const SideMenuItem = styled(Link)<{ isActive: boolean }>`
  text-decoration: none;
  padding: ${Spacing.s};
  color: ${({ theme }) => theme.sub};
  display: flex;
  align-items: center;
  gap: ${Spacing.s};
  border-radius: ${Rounded.md};
  font-size: calc(${FontSize.m} - 0.1rem);
  color: ${({ theme }) => theme.main};
  margin: auto 0;
  width: 100%;

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  svg {
    width: 1.5rem;
  }

  &,
  & * {
    transition: all 0.1s !important;
  }

  ${({ theme, isActive }) =>
    isActive &&
    `
      background-color: ${theme.main};
      color: ${theme.sub};
    `}

  &:hover {
    background-color: ${({ theme }) => theme.lightMain};
    color: ${({ theme }) => theme.sub};
  }

  @media (max-width: ${Breakpoints.DESKTOP}) {
    width: 4rem;
    justify-content: center;

    span {
      display: none;
    }
  }
`;

type PageSideMenuProps = {
  links: { link: string; title: string; icon: IconProp }[];
};

export const PageSideMenu: React.FC<PageSideMenuProps> = ({ links = [] }) => {
  const location = useLocation();
  const { width } = useWindowDimensions();

  const psas = useAppSelector(userSliceSelectors.selectLastUnreadPSAs);

  return (
    <Flex position="relative" flex={1}>
      <SideMenu isTherePSA={!isEmpty(psas)}>
        {links.map((link) => (
          <Flex
            key={link.link}
            height={"3rem"}
            width={"100%"}
            padding="s"
            dataTestId={TestIDs.general.subSideMenu.link(
              link.link.split("/")[2],
            )}
          >
            <Tooltip
              title={
                width <= Number(Breakpoints.DESKTOP.replace("px", ""))
                  ? link.title
                  : undefined
              }
              key={link.link}
            >
              <SideMenuItem
                to={link.link}
                isActive={location.pathname === link.link}
              >
                <AtiraIcon icon={link.icon} />
                <span>{link.title}</span>
              </SideMenuItem>
            </Tooltip>
          </Flex>
        ))}
      </SideMenu>

      <GlobalStyle />
    </Flex>
  );
};
